import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { environment } from 'environments/environment';
import { Observable, catchError, throwError } from 'rxjs';

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);

    // Clone the request object
    let newReq = req.clone();
    const isAWSS3Request = req.url.includes('amazonaws.com');

    if (
        authService.accessToken &&
        !AuthUtils.isTokenExpired(authService.accessToken) &&
        !isAWSS3Request
    ) {
        newReq = req.clone({
            headers: req.headers.set(
                'Authorization',
                'Bearer ' + authService.accessToken
            ),
        });

        // ⚠️ TEMPORAL (solo dev local): cuando la petición va a un servicio
        // enrutado al pod local vía proxy (environment.localProxyServices), no
        // pasa por Kong, así que emulamos la cabecera de identidad
        // `x-consumer-custom-id` que el gateway inyecta en producción,
        // derivándola del claim `iss` del JWT. En producción `localProxyServices`
        // no existe y este bloque es un no-op.
        //
        // `authApiUrlOverride` tiene que estar en esta lista: el logout
        // (`DELETE /jwt`) es el ÚNICO endpoint de auth que resuelve su
        // identidad desde la cabecera, y su URL (`/auth-camilo/jwt`) no
        // deriva de `localProxyServices`, así que sin esta entrada no
        // recibía `x-consumer-custom-id` y el servicio respondía 401 de
        // forma silenciosa. El login no se veía afectado porque `POST /jwt`
        // lleva las credenciales en el body.
        const env = environment as unknown as {
            localProxyServices?: string[];
            profileApiUrlOverride?: string;
            authApiUrlOverride?: string;
        };
        const localPrefixes = [
            ...(env.localProxyServices ?? []).map((name) => `/${name}/`),
            ...(env.profileApiUrlOverride ? [env.profileApiUrlOverride] : []),
            ...(env.authApiUrlOverride?.startsWith('/')
                ? [env.authApiUrlOverride]
                : []),
        ];
        if (localPrefixes.some((prefix) => newReq.url.startsWith(prefix))) {
            const userId = AuthUtils.getUserIdFromToken(authService.accessToken);
            const identityHeaders = userId
                ? newReq.headers.set('x-consumer-custom-id', userId)
                : newReq.headers;

            // El dev-server de Angular (Vite) no proxea correctamente las URLs
            // que llevan una barra final justo antes del query string (`/?`),
            // respondiendo 404. Colapsamos `/?` → `?`; el pod matchea la ruta
            // con o sin barra final, así que es seguro.
            newReq = newReq.clone({
                url: newReq.url.replace('/?', '?'),
                headers: identityHeaders,
            });
        }
    }

    // Response
    return next(newReq).pipe(
        catchError((error) => {
            // Catch "403 Forbidden" responses
            if (error instanceof HttpErrorResponse && error.status === 403 && !isAWSS3Request) {
                // No cerrar sesión si es un error de change-password (puede ser restricción de política)
                const isChangePasswordRequest = req.url.includes('/users/change-password');
                if (!isChangePasswordRequest) {
                    authService.signOut(error).subscribe();
                }
            }
            // Catch "401 Unauthorized" responses
            if (error instanceof HttpErrorResponse && error.status === 401) {
                // No interceptar 401 en la propia llamada de signOut (DELETE /jwt)
                // para evitar bucle infinito
                const isSignOutRequest = req.method === 'DELETE' && req.url.includes('/jwt');
                // No interceptar 401 en change-password (significa contraseña actual incorrecta, no token inválido)
                const isChangePasswordRequest = req.url.includes('/users/change-password');
                const pathActual = window.location.pathname;

                if (!isSignOutRequest && !isChangePasswordRequest && !pathActual.includes('sign-in')) {
                    // Limpiar token y redirigir a sign-in sin recargar
                    localStorage.removeItem('accessToken');
                    authService.signOut().subscribe();
                }
            }

            return throwError(error);
        })
    );
};
